import { PlusSquareIcon, TrashIcon, WarningIcon } from '@phosphor-icons/react';
import { ColumnDef } from '@tanstack/react-table';
import { CircleCheckIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import colors from 'tailwindcss/colors';

import { Button } from '@/components/ui/button';
import { FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Autocomplete,
  GenericTable,
  ItemStatusEnum,
  Spinner,
} from '@/modules/common';
import { CreateGcrDialog } from '@/modules/gcrs/components';

import { useFetchModelsData } from '../api';
import { EventPrinterStatus, positionOptions } from '../constants';
import { getModelGcrFileDetails } from '../helpers';
import { EventPrinter } from '../types';

interface ModelsSelectionProps {
  defaultPrinters: EventPrinter[];
  defaultValue: { modelId: string }[];
  onRedirectToPrinters: () => void;
  onValidate: (value: boolean) => void;
}

type TableDataType = {
  modelId: string;
  position: string;
  sizeId: string;
  quantity: string;
};

export function ModelsSelection({
  defaultPrinters,
  defaultValue,
  onValidate,
  onRedirectToPrinters,
}: ModelsSelectionProps) {
  const { control, watch } = useFormContext();
  const [createGcrDetails, setCreateGcrDetails] = useState(null);
  const { optionsData, models, printers, gcrs, isFetching, refetchGcrs } =
    useFetchModelsData();
  const [activeFocusFieldId, setActiveFocusFieldId] = useState<string>('');

  const selectedPrinters = watch('printers', defaultPrinters);
  const filteredPrinters = selectedPrinters.filter(
    (printer) =>
      printer.printerId && printer.status === EventPrinterStatus.Enabled
  );

  const fields = watch('models', defaultValue);
  const { append, remove } = useFieldArray({
    control,
    name: 'models',
  });

  const filteredData = useMemo(
    () => models.filter((item) => item.status === ItemStatusEnum.Ready),
    [models]
  );

  const getModelSelectedValue = useCallback(
    (modelId: string) => models.find((item) => item.id === modelId)?.name ?? '',
    [models]
  );

  const modelOptions = useMemo(
    () =>
      filteredData?.map((style) => ({
        value: style.name,
        id: style.id,
      })),
    [filteredData]
  );

  const gcrModelTypeMapping = Object.fromEntries(
    fields
      .filter((field) => field.modelId && field.sizeId)
      .map((field) => {
        const { typeId, emptyGcrFilePrinter } = getModelGcrFileDetails({
          models,
          printers,
          sizeId: field.sizeId,
          modelId: field.modelId,
          gcrs: gcrs.items,
          eventPrinters: filteredPrinters,
        });

        return [
          `${field.modelId}_${field.sizeId}`,
          { typeId, emptyGcrFilePrinter },
        ];
      })
  );

  useEffect(() => {
    const emptyGcrFilePrinter = Object.values(gcrModelTypeMapping).find(
      (item) => item.emptyGcrFilePrinter
    );

    onValidate(!!emptyGcrFilePrinter);
  }, [gcrModelTypeMapping]);

  const redirectToGcrForm = (
    typeId: string,
    sizeId: string,
    printerId: string
  ) => {
    setCreateGcrDetails({
      typeId,
      sizeId,
      printerId,
    });
  };

  const columns: ColumnDef<TableDataType>[] = [
    {
      size: 210,
      accessorKey: 'name',
      header: 'Model type',
      cell: ({ row }) => (
        <Autocomplete
          name={`models.${row.index}.modelId`}
          control={control}
          searchable
          placeholder="Select model type"
          options={modelOptions}
          className="w-[210px] border-0"
          defaultValue={getModelSelectedValue(row.original.modelId)}
        />
      ),
    },
    {
      size: 140,
      accessorKey: 'sizeId',
      header: 'Size',
      cell: ({ row }) => (
        <Autocomplete
          name={`models.${row.index}.sizeId`}
          control={control}
          placeholder="Select size"
          options={optionsData.sizes}
          className="w-[140px] border-0"
        />
      ),
    },
    {
      size: 180,
      accessorKey: 'position',
      header: 'Position',
      cell: ({ row }) => (
        <Autocomplete
          name={`models.${row.index}.position`}
          control={control}
          placeholder="Select position"
          options={positionOptions}
          className="w-[180px] border-0"
        />
      ),
    },
    {
      size: 150,
      accessorKey: 'quantity',
      header: 'Quantity',
      cell: ({ row }) => {
        const fieldName = `models.${row.index}.quantity`;
        return (
          <FormField
            control={control}
            name={fieldName}
            render={({ field }) => (
              <FormItem>
                <Input
                  {...field}
                  type="number"
                  autoFocus={fieldName === activeFocusFieldId}
                  onFocus={() => setActiveFocusFieldId(fieldName)}
                  onBlur={() => setActiveFocusFieldId('')}
                  placeholder="Add quantity"
                  className="w-[140px] border-0"
                  step={1}
                />
                <FormMessage />
              </FormItem>
            )}
          />
        );
      },
    },
    {
      accessorKey: 'gcrFile',
      header: 'GCR File',
      cell: ({ row }) => {
        if (!row.original.modelId || !row.original.sizeId) {
          return null;
        }

        if (!filteredPrinters.length) {
          return (
            <Button
              variant="link"
              className="text-destructive"
              startIcon={<WarningIcon />}
              onClick={onRedirectToPrinters}
            >
              Please add or anable printers for this model
            </Button>
          );
        }

        const { typeId, emptyGcrFilePrinter } =
          gcrModelTypeMapping[
            `${row.original.modelId}_${row.original.sizeId}`
          ] ?? [];

        return emptyGcrFilePrinter ? (
          <Button
            variant="link"
            className="text-destructive"
            startIcon={<WarningIcon />}
            onClick={() =>
              redirectToGcrForm(
                typeId,
                row.original.sizeId,
                emptyGcrFilePrinter.id
              )
            }
          >
            Where is no gcr file for {emptyGcrFilePrinter.value} printer
          </Button>
        ) : (
          <span>
            <CircleCheckIcon color={colors.green[400]} size={28} />
          </span>
        );
      },
    },
    {
      size: 40,
      id: 'actions',
      enableHiding: false,
      cell: ({ row }) => (
        <Button
          variant="ghost"
          data-testid="remove-model-button"
          size="icon"
          onClick={() => remove(row.index)}
        >
          <TrashIcon size={28} color={colors.zinc[400]} />
        </Button>
      ),
    },
  ];

  const addNewEmptyModel = () => {
    append({
      modelId: '',
      position: '',
      sizeId: '',
      quantity: '',
    });
  };

  return (
    <div>
      {isFetching ? (
        <Spinner />
      ) : (
        <>
          <GenericTable
            columns={columns}
            data={fields}
            key="models-table"
            name="models"
            emptyCellClassName="h-20"
            tableCellClassName="py-1"
            tableRowClassName="hover:bg-transparent"
            emptyDescription="No models added. Please add new model."
          />
          <Button
            disabled={isFetching}
            variant="transparent"
            data-testid="add-model-button"
            onClick={addNewEmptyModel}
            className="mt-4"
            startIcon={<PlusSquareIcon />}
          >
            Add model
          </Button>
        </>
      )}

      {createGcrDetails && (
        <CreateGcrDialog
          isOpen
          onSuccess={() => refetchGcrs()}
          typeId={createGcrDetails?.typeId}
          sizeId={createGcrDetails?.sizeId}
          printerId={createGcrDetails?.printerId}
          onOpenChange={() => setCreateGcrDetails(null)}
        />
      )}
    </div>
  );
}
